import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  getCapabilityCoverage,
  type RunnerCapabilityCoverage,
} from '@api/localRunners';
import useAuth from '@contexts/useAuth';

export interface UseCapabilityCoverageArgs {
  /** Required apps for the selected local agent (empty → hook disabled). */
  apps: readonly string[];
  /** Named assignees when assigneeType is USER. */
  userIds?: readonly string[];
  /** Selected group when assigneeType is GROUP. */
  groupId?: string;
}

/**
 * Non-blocking editor hint: does the chosen assignee set own a runner
 * (online or offline) that can run the selected local agent? Disabled
 * until there is at least one required app AND at least one assignee
 * target, so cloud nodes and half-configured local nodes issue no query.
 */
export function useCapabilityCoverage(
  args: UseCapabilityCoverageArgs,
): UseQueryResult<RunnerCapabilityCoverage, Error> {
  const { user } = useAuth();
  const token = user?.token;
  const hasApps = args.apps.length > 0;
  const hasTarget = (args.userIds?.length ?? 0) > 0 || Boolean(args.groupId);
  const enabled = Boolean(token) && hasApps && hasTarget;

  return useQuery<RunnerCapabilityCoverage, Error>({
    queryKey: [
      'capability-coverage',
      [...args.apps].sort((a, b) => a.localeCompare(b)),
      [...(args.userIds ?? [])].sort((a, b) => a.localeCompare(b)),
      args.groupId ?? '',
    ],
    queryFn: () =>
      getCapabilityCoverage({
        apps: args.apps,
        userIds: args.userIds,
        groupId: args.groupId,
      }),
    enabled,
    staleTime: 30_000,
    retry: 1,
  });
}
